import { useState, useEffect, useCallback, useRef } from "react";
import "./trainers.css";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Docenti = () => {
  const [loading, setLoading] = useState(false);
  const [docenti, setDocenti] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const navigate = useNavigate();
  const downloadRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, "0")}.${String(
      date.getMonth() + 1
    ).padStart(2, "0")}.${date.getFullYear()}`;
  };

  const formatBoolean = (value) => (value ? "Si" : "No");

  const fetchDocenti = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
    setLoading(true);
    try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get(`/trainers`, {
          params,
        });

        const { content = [], page: pageInfo } = response.data ?? {};

      const formattedDocenti = content.map((trainer) => ({
        ...trainer,
        dataCreazione: formatDate(trainer.dataCreazione),
        dataUltimoFollowup: formatDate(trainer.dataUltimoFollowup),
        dataAggiornamentoTitoliStudio: formatDate(
          trainer.dataAggiornamentoTitoliStudio
        ),
        dataAggiornamentoEsperienzeLavorative: formatDate(
          trainer.dataAggiornamentoEsperienzeLavorative
        ),
        dataAggiornamentoCv: formatDate(trainer.dataAggiornamentoCv),
        dataUltimoModuloProtocollato: formatDate(
          trainer.dataUltimoModuloProtocollato
        ),
        dataAggiornamentoDisponibilità: formatDate(
          trainer.dataAggiornamentoDisponibilità
        ),
        dataUltimoAccordoQuadro: formatDate(trainer.dataUltimoAccordoQuadro),
        dataUltimaCandidatura: formatDate(trainer.dataUltimaCandidatura),
        intervistaPresente: formatBoolean(trainer.intervistaPresente),
        simulazionePresente: formatBoolean(trainer.simulazionePresente),
        mansioni: trainer.mansioni || "",
      }));

      setDocenti(formattedDocenti);
        setTotalRows(pageInfo?.totalElements ?? content.length);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per visualizzare i Docenti!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchDocenti();
    }
  }, [navigate, fetchDocenti]);

  const handleRowClick = (trainer) => {
    navigate(`/docente/${trainer.id}`);
  };

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/trainers", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `docenti.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const columns = [
    { field: "dataCreazione", header: "Data Creazione" },
    { field: "codiceFiscale", header: "Codice Fiscale" },
    { field: "cognome", header: "Cognome" },
    { field: "nome", header: "Nome" },
    { field: "cittàDiResidenza", header: "Città Di Residenza" },
    { field: "cellulare", header: "Cell" },
    { field: "email", header: "Mail" },
    { field: "mansioni", header: "Mansioni", size: 300 },
    { field: "dataUltimoFollowup", header: "Data Ultimo Follow-up", size: 300 },
    {
      field: "dataAggiornamentoTitoliStudio",
      header: "Data Aggiornamento Titoli di Studio",
      size: 400,
    },
    {
      field: "dataAggiornamentoEsperienzeLavorative",
      header: "Data Aggiornamento Esperienze Lavorative",
      size: 470,
    },
    { field: "intervistaPresente", header: "Intervista" },
    { field: "simulazionePresente", header: "Simulazione" },
    {
      field: "dataAggiornamentoCv",
      header: "Data Aggiornamento Cv",
      size: 280,
    },
    {
      field: "dataUltimoModuloProtocollato",
      header: "Data Ultimo Modulo Protocollato",
      size: 400,
    },
    {
      field: "dataAggiornamentoDisponibilità",
      header: "Data Aggiornamento Disponibilità",
      size: 400,
    },
    {
      field: "dataUltimoAccordoQuadro",
      header: "Data Ultimo Accordo Quadro",
      size: 350,
    },
    {
      field: "dataUltimaCandidatura",
      header: "Data Ultima Candidatura",
      size: 300,
    },
  ];

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Docenti</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-content Docenti">
        <Table
          columns={columns}
          data={docenti}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default Docenti;
